import React from "react";
import { X, Search, MapPin, Loader2, Building2 } from "lucide-react";

// ============================================================
// 1. CREATE PINCODE TEAM MODAL
// ============================================================

export function CreatePincodeTeamModal({
  open = false,
  loading = false,
  form,
  search = "",
  options = [],
  searchLoading = false,
  dropdownOpen = false,
  selectedPincode,
  onChange,
  onSearchChange,
  onSearchFocus,
  onSelectPincode,
  onCreate,
  onClose,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Create Pincode Team
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Create a team with pincode coverage and driver capacity rules.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* FORM */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* TEAM NAME */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Team Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.teamName || ""}
                onChange={(e) => onChange("teamName", e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                placeholder="Enter team name"
              />
            </div>

            {/* PINCODE */}
            <div className="relative">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Pincode <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onFocus={() => {
                    if (search && !selectedPincode) onSearchFocus();
                  }}
                  className="w-full rounded-lg border px-3 py-2 pr-10 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="Type pincode to search"
                />

                {searchLoading && (
                  <Loader2
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
                  />
                )}
              </div>

              {selectedPincode && form?.pinCode && (
                <p className="mt-1 text-[11px] text-emerald-600">
                  Selected pincode: {search}
                </p>
              )}

              {/* PINCODE DROPDOWN */}
              {dropdownOpen && !selectedPincode && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-lg border bg-white shadow-lg">
                  {searchLoading ? (
                    <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-gray-500">
                      <Loader2 size={14} className="animate-spin" />
                      Searching pincodes...
                    </div>
                  ) : options.length > 0 ? (
                    options.map((item, index) => {
                      const optionId = getPincodeId(item);
                      const optionLabel = getPincodeLabel(item);
                      const detailRows = getPincodeDetailRows(item);

                      return (
                        <button
                          key={optionId || `${optionLabel}-${index}`}
                          type="button"
                          onClick={() => onSelectPincode(item)}
                          className="w-full border-b px-3 py-3 text-left last:border-b-0 hover:bg-emerald-50"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <MapPin
                                size={14}
                                className="mt-0.5 shrink-0 text-emerald-600"
                              />
                              <span className="text-sm font-semibold text-gray-900">
                                {optionLabel}
                              </span>
                            </div>

                            {optionId !== "" && optionId != null && (
                              <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                ID: {optionId}
                              </span>
                            )}
                          </div>

                          {detailRows.length > 0 && (
                            <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-lg bg-gray-50 p-2.5 sm:grid-cols-2">
                              {detailRows.map(({ key, label, value }) => (
                                <div
                                  key={key}
                                  className="min-w-0 text-[11px] leading-4"
                                >
                                  <span className="font-medium text-gray-500">
                                    {label}:
                                  </span>{" "}
                                  <span className="break-words text-gray-800">
                                    {value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })
                  ) : search ? (
                    <div className="px-3 py-4 text-center text-xs text-gray-500">
                      No pincode found
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* ADDRESS */}
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                value={form?.address || ""}
                onChange={(e) => onChange("address", e.target.value)}
                className="w-full resize-none rounded-lg border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                placeholder="Enter coverage address / area"
              />
            </div>

            {/* MINIMUM DRIVERS */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Minimum Drivers <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                value={form?.minimumDrivers ?? ""}
                onChange={(e) => onChange("minimumDrivers", e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                placeholder="e.g. 2"
              />
            </div>

            {/* MAXIMUM DRIVERS */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Maximum Drivers <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                value={form?.maximumDrivers ?? ""}
                onChange={(e) => onChange("maximumDrivers", e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                placeholder="e.g. 10"
              />
            </div>

            {/* ACCURACY */}
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Accuracy <span className="text-red-500">*</span>
              </label>
              <select
                value={form?.accuracy || ""}
                onChange={(e) => onChange("accuracy", e.target.value)}
                className="w-full rounded-lg border bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              >
                <option value="">Select accuracy</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* FOOTER */}
          <div className="mt-6 flex justify-end gap-2 border-t pt-4">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={loading}
              onClick={onCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <MapPin size={15} />
                  Create Pincode Team
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 2. ASSIGN SHOPS MODAL
// ============================================================

export function AssignShopsModal({
  open = false,
  team,
  search = "",
  shops = [],
  workingCode = "",
  onSearchChange,
  onAssign,
  onClose,
}) {
  if (!open) return null;

  const teamName = team?.team_name || "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-lg">
        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-3">
          <div>
            <h3 className="text-lg font-semibold">Assign Shops</h3>
            <p className="text-xs text-gray-500">
              Team: <span className="font-medium">{teamName}</span>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded p-2 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex min-h-0 flex-1 flex-col p-5">
          {/* SEARCH */}
          <div className="mb-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Search size={16} />
              </span>

              <input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search shop by name, code, or address..."
                className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm"
              />
            </div>
          </div>

          {/* SHOP TABLE */}
          <div className="max-h-[60vh] min-h-0 overflow-auto rounded-lg border">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Shop</th>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Address</th>
                  <th className="px-3 py-2 text-left">Phone</th>
                  <th className="px-3 py-2 text-left">Team (current)</th>
                  <th className="w-40 px-3 py-2 text-left">Action</th>
                </tr>
              </thead>

              <tbody>
                {shops.map((shop) => {
                  const assignedTo = String(shop.teamName || "").trim();

                  const assignedElsewhere =
                    Boolean(assignedTo) &&
                    assignedTo.toLowerCase() !== teamName.toLowerCase();

                  const alreadyInThisTeam = (team?.shopList || []).some(
                    (item) => item.shop_code === shop.shop_code,
                  );

                  const disabled = assignedElsewhere || alreadyInThisTeam;
                  const disabledReason = assignedElsewhere
                    ? `Assigned to: ${assignedTo}`
                    : alreadyInThisTeam
                      ? "Already in this team"
                      : "";

                  const isWorking =
                    String(workingCode) === String(shop.shop_code);

                  return (
                    <tr key={shop.id || shop.shop_code} className="border-t">
                      <td className="px-3 py-2 font-medium">
                        {shop.shop_name}
                      </td>
                      <td className="px-3 py-2">{shop.shop_code}</td>
                      <td className="px-3 py-2">
                        <div className="max-w-[360px] truncate">
                          {shop.shop_address}
                        </div>
                      </td>
                      <td className="px-3 py-2">{shop.shop_phone}</td>
                      <td className="px-3 py-2">
                        {assignedTo || "—"}

                        {assignedElsewhere && (
                          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                            Assigned to: {assignedTo}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          disabled={disabled || isWorking}
                          onClick={() => onAssign(shop)}
                          title={disabledReason}
                          className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-xs text-white ${
                            disabled
                              ? "cursor-not-allowed bg-gray-300"
                              : "bg-emerald-600 hover:bg-emerald-700"
                          }`}
                        >
                          {isWorking ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Assigning...
                            </>
                          ) : (
                            <>
                              <Building2 size={14} />
                              Assign
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {shops.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No shops found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* FOOTER */}
          <div className="mt-4 flex shrink-0 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-gray-200 px-4 py-2 text-sm hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 3. PINCODE HELPER FUNCTIONS
// ============================================================

export function extractPincodeOptions(data) {
  const findArray = (value, depth = 0) => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object" || depth > 3) return null;

    const keys = [
      "response",
      "data",
      "items",
      "list",
      "records",
      "result",
      "results",
      "pincodeDetails",
      "pincodes",
      "pinCodes",
    ];

    for (const key of keys) {
      const found = findArray(value[key], depth + 1);
      if (found) return found;
    }

    return null;
  };

  return findArray(data) || [];
}

export function getPincodeId(item) {
  return (
    item?.id ??
    item?.pincodeId ??
    item?.pinCodeId ??
    item?.pincode_id ??
    item?.pin_code_id ??
    item?.pk_pincode_id ??
    item?.pkPincodeId ??
    item?.pk_id ??
    ""
  );
}

export function getPincodeLabel(item) {
  const value =
    item?.pincode ??
    item?.pinCode ??
    item?.pin_code ??
    item?.code ??
    item?.postalCode ??
    item?.postal_code ??
    item?.value ??
    item?.name ??
    getPincodeId(item);

  return String(value ?? "");
}

export function getPincodeAddress(item) {
  if (!item || typeof item !== "object") return "";

  const directAddress =
    item?.address ??
    item?.fullAddress ??
    item?.full_address ??
    item?.areaAddress ??
    item?.area_address ??
    item?.locality ??
    item?.area ??
    item?.areaName ??
    item?.area_name ??
    item?.officeName ??
    item?.office_name ??
    item?.postOffice ??
    item?.post_office ??
    "";

  if (String(directAddress || "").trim()) {
    return String(directAddress).trim();
  }

  const parts = [
    item?.localityName ?? item?.locality_name,
    item?.block ?? item?.taluk ?? item?.tehsil,
    item?.city ?? item?.cityName ?? item?.city_name,
    item?.district ?? item?.districtName ?? item?.district_name,
    item?.state ?? item?.stateName ?? item?.state_name,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return [...new Set(parts)].join(", ");
}

function getPincodeDetailRows(item) {
  if (!item || typeof item !== "object") return [];

  const hiddenKeys = new Set([
    "id",
    "pincodeid",
    "pincode_id",
    "pin_code_id",
    "pk_pincode_id",
    "pkpincodeid",
    "pk_id",
    "pincode",
    "pin_code",
    "code",
    "postalcode",
    "postal_code",
  ]);

  return Object.entries(item)
    .filter(([key, value]) => {
      if (hiddenKeys.has(String(key).toLowerCase())) return false;
      if (value === null || value === undefined || value === "") return false;
      return ["string", "number", "boolean"].includes(typeof value);
    })
    .map(([key, value]) => ({
      key,
      label: formatPincodeDetailLabel(key),
      value: String(value),
    }));
}

function formatPincodeDetailLabel(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
